import React from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Avatar,
  Stack,
  Button,
} from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import VisibilityIcon from "@mui/icons-material/Visibility";
import HubIcon from "@mui/icons-material/Hub";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import SectionHeading from "./SectionHeading";
import useReveal from "./useReveal";
import { brand, gradients } from "../theme";

const pillars = [
  {
    icon: <FlagIcon />,
    title: "Our Mission",
    text: "Deliver real-time, outcome-driven technology training that meets exacting industry requirements — without compromise.",
  },
  {
    icon: <VisibilityIcon />,
    title: "Our Vision",
    text: "Build a high-performance tech ecosystem that confidently prepares the workforce of tomorrow across 200+ partner campuses by 2030.",
  },
  {
    icon: <HubIcon />,
    title: "Our Model",
    text: "An active dual ecosystem where live commercial software engineering continuously feeds our technology workforce training.",
  },
];

const team = [
  { name: "Razak Mohamed S", title: "Founder & Chief Technology Officer", color: brand.blue },
  { name: "Rasheedha R", title: "Operations Head", color: brand.orange },
  { name: "Annamalai", title: "Development Manager", color: brand.blueDark },
];

const initials = (name) =>
  name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("");

const About = () => {
  const ref = useReveal();

  return (
    <section id="about-section" className="about-section" ref={ref}>
      <Box sx={{ background: gradients.soft, py: { xs: 7, md: 11 } }}>
        <Container>
          <SectionHeading
            eyebrow="WHO WE ARE"
            title="A Technology Transformation Partner"
            subtitle="Headquartered in Salem, Tamil Nadu, Zealous Tech Corp is a premium technology consulting, custom software design, and institutional capability-building organisation — operating as a strategic product engineering company, never a conventional coaching centre."
          />

          {/* Narrative + pillars */}
          <Grid container spacing={4} alignItems="stretch" className="reveal">
            <Grid item xs={12} md={6}>
              <Box sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <Typography variant="body1" sx={{ color: brand.slate, mb: 2 }}>
                  Since 2018, we have scaled systematically from an institutional bootcamp
                  delivery partner into a comprehensive software development, product
                  architecture, and capability-building company. The industry expects
                  day-one readiness, clean-code architectural alignment, and intuitive
                  problem-solving — and we exist to close that gap.
                </Typography>
                <Typography variant="body1" sx={{ color: brand.slate, mb: 2 }}>
                  Our corporate wing houses capable software developers who continuously
                  deliver web applications, enterprise ERP networks, and scalable SaaS
                  infrastructures for commercial clients. The architectural patterns and
                  data complexities they face in the field are passed natively into our
                  training portfolio — so students interact directly with
                  production-grade environments, not just syntax.
                </Typography>
                <Typography variant="body1" sx={{ color: brand.slate }}>
                  Backed by solid service-level agreements, we offer institutional
                  decision-makers complete transparency and accountability at every
                  stage of partnership.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack spacing={2.5}>
                {pillars.map((p) => (
                  <Paper
                    key={p.title}
                    elevation={0}
                    sx={{
                      p: 3,
                      display: "flex",
                      gap: 2,
                      alignItems: "flex-start",
                      border: "1px solid rgba(12,131,200,0.12)",
                      background: "#fff",
                      transition: "transform .25s ease, box-shadow .25s ease",
                      "&:hover": { transform: "translateY(-4px)", boxShadow: "0 16px 36px rgba(15,39,64,0.10)" },
                    }}
                  >
                    <Box
                      sx={{
                        flexShrink: 0,
                        width: 52,
                        height: 52,
                        borderRadius: "14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: gradients.brand,
                        color: "#fff",
                      }}
                    >
                      {p.icon}
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ color: brand.navy, mb: 0.5 }}>
                        {p.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: brand.slate }}>
                        {p.text}
                      </Typography>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            </Grid>
          </Grid>

          {/* Founder message */}
          <Paper
            elevation={0}
            className="reveal"
            sx={{
              mt: { xs: 5, md: 7 },
              p: { xs: 3, md: 5 },
              background: gradients.navy,
              color: "#fff",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <FormatQuoteIcon
              sx={{ position: "absolute", top: 16, right: 24, fontSize: 120, opacity: 0.08 }}
            />
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={3} sx={{ textAlign: "center" }}>
                <Avatar
                  sx={{
                    width: 120,
                    height: 120,
                    mx: "auto",
                    bgcolor: brand.orange,
                    fontSize: "2.4rem",
                    fontWeight: 800,
                    border: "4px solid rgba(255,255,255,0.2)",
                  }}
                >
                  RM
                </Avatar>
              </Grid>
              <Grid item xs={12} md={9}>
                <Typography
                  variant="overline"
                  sx={{ color: brand.orangeLight, letterSpacing: "0.12em" }}
                >
                  FOUNDER'S MESSAGE
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 400, fontStyle: "italic", my: 1.5, lineHeight: 1.6 }}>
                  “We are not a computer institute or a legacy coaching center. We embed
                  real-world engineering pipelines directly inside our partner
                  institutions — guaranteeing students don't just memorise syntax, but
                  build the confidence of production-grade engineers.”
                </Typography>
                <Typography sx={{ fontWeight: 700 }}>Razak Mohamed</Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.9rem", mb: 2 }}>
                  Founder &amp; Chief Technology Officer, Zealous Tech Corp
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  startIcon={<LinkedInIcon />}
                  href="https://www.linkedin.com/in/razakmohamed"
                  target="_blank"
                  rel="noopener"
                >
                  Connect on LinkedIn
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Leadership team */}
          <Box sx={{ mt: { xs: 6, md: 8 } }} className="reveal">
            <Typography
              variant="h5"
              align="center"
              sx={{ color: brand.navy, fontWeight: 700, mb: 4 }}
            >
              Leadership Team
            </Typography>
            <Grid container spacing={3} justifyContent="center">
              {team.map((member) => (
                <Grid item xs={12} sm={6} md={4} key={member.name}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      textAlign: "center",
                      border: "1px solid rgba(12,131,200,0.12)",
                      transition: "transform .25s ease, box-shadow .25s ease",
                      "&:hover": { transform: "translateY(-6px)", boxShadow: "0 18px 40px rgba(15,39,64,0.12)" },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 84,
                        height: 84,
                        mx: "auto",
                        mb: 2,
                        bgcolor: member.color,
                        fontSize: "1.6rem",
                        fontWeight: 800,
                      }}
                    >
                      {initials(member.name)}
                    </Avatar>
                    <Typography variant="h6" sx={{ color: brand.navy }}>
                      {member.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: brand.orange, fontWeight: 600 }}>
                      {member.title}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>
    </section>
  );
};

export default About;
